import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { Users, Plus, Search, Edit, Trash2, Phone, Mail, MapPin, Eye, Building, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { customerApiService, Customer } from "../services";
import { PermissionGate } from "./ui/PermissionGate";

const SEARCH_DEBOUNCE_MS = 400;

export function CustomerListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Customer management route (from backend permissions)
  const CUSTOMER_ROUTE = '/customers';

  // Debounce search term: update debouncedSearchTerm after user stops typing
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  // Load customers from API (uses debounced search term to avoid request per keystroke)
  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      
      let response;
      if (debouncedSearchTerm) {
        response = await customerApiService.searchCustomers(debouncedSearchTerm, currentPage, pageSize);
      } else {
        response = await customerApiService.getCustomers(currentPage, pageSize);
      }
      
      if (response.success) {
        setCustomers(response.data);
        
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.totalCount);
        } else {
          setTotalPages(1);
          setTotalCount(response.data.length);
        }
      }
    } catch (error) {
      setCustomers([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, currentPage, pageSize]);

  // Load customers when debounced search or page changes
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Handle page changes
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleNewCustomer = () => {
    navigate('/customers/new');
  };

  const handleEditCustomer = (customer: Customer) => {
    navigate(`/customers/${customer.id}/edit`);
  };

  const handleViewCustomer = (customer: Customer) => {
    navigate(`/customers/${customer.id}/view`);
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    try {
      await customerApiService.deleteCustomer(customer.id.toString());
      await loadCustomers();
      toast.success('Customer deleted successfully');
    } catch (error) {
      // console.error('Failed to delete customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  const getStatusColor = (isActive: boolean) => {
    if (isActive) {
      return 'bg-green-100 text-green-700 border-green-200';
    } else {
      return 'bg-red-100 text-red-700 border-red-200';
    }
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    const aCode = (a.customerCode || '').trim();
    const bCode = (b.customerCode || '').trim();
    const aMatch = aCode.match(/\d+/);
    const bMatch = bCode.match(/\d+/);
    if (aMatch && bMatch) {
      return Number(bMatch[0]) - Number(aMatch[0]);
    }
    return bCode.localeCompare(aCode);
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Customer Management</CardTitle>
                <p className="text-primary-100 text-sm">Manage your customer information and contacts</p>
              </div>
            </div>
            <PermissionGate route={CUSTOMER_ROUTE} action="create">
              <Button
                onClick={handleNewCustomer}
                className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Customer
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {searchTerm ? `${totalCount} customers found` : `${totalCount} customers found`}
            </div>
          </div>

          {/* Customers Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white shadow-sm">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                <span className="ml-2 text-gray-600">Loading customers...</span>
              </div>
            ) : customers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No customers found' : 'No customers yet'}
                </h3>
                <p className="text-gray-500 text-center max-w-md">
                  {searchTerm 
                    ? `No customers match your search for "${searchTerm}". Try a different search term.`
                    : 'Get started by adding your first customer using the "New Customer" button.'
                  }
                </p>
                {!searchTerm && (
                  <PermissionGate route={CUSTOMER_ROUTE} action="create">
                    <Button
                      onClick={handleNewCustomer}
                      className="mt-4 bg-primary-600 hover:bg-primary-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Customer
                    </Button>
                  </PermissionGate>
                )}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary-50">
                      <TableHead className="font-semibold text-primary-800">Customer ID</TableHead>
                      <TableHead className="font-semibold text-primary-800">Customer Details</TableHead>
                      <TableHead className="font-semibold text-primary-800">Contact Info</TableHead>
                      <TableHead className="font-semibold text-primary-800">GST & PAN</TableHead>
                      <TableHead className="font-semibold text-primary-800">Location</TableHead>
                      <TableHead className="font-semibold text-primary-800">Status</TableHead>
                      <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedCustomers.map((customer: Customer) => (
                      <TableRow key={customer.id} className="hover:bg-primary-50/50">
                        <TableCell>
                          <div className="font-medium text-gray-900">
                            {customer.customerCode || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-semibold text-gray-900">{customer.customerName || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{customer.firmName || 'N/A'}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-600">{customer.email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-3 w-3 text-gray-400" />
                              <span className="text-gray-600">{customer.mobileNo || 'N/A'}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="text-sm text-gray-600">
                              GST: {customer.gstNo || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-600">
                              PAN: {customer.pan_no || 'N/A'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-3 w-3 text-gray-400" />
                            <span className="text-gray-600">{customer.stateName || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(customer.isActive)}>
                            {customer.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <PermissionGate route={CUSTOMER_ROUTE} action="view">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewCustomer(customer)}
                                className="h-8 w-8 p-0 border-purple-200 text-purple-600 hover:bg-purple-50"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </PermissionGate>
                            <PermissionGate route={CUSTOMER_ROUTE} action="edit">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditCustomer(customer)}
                                className="h-8 w-8 p-0 border-purple-200 text-purple-600 hover:bg-purple-50"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </PermissionGate>
                            <PermissionGate route={CUSTOMER_ROUTE} action="delete">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteCustomer(customer)}
                                className="h-8 w-8 p-0 border-red-200 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </PermissionGate>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="border-t border-primary-200 bg-primary-50/30 p-4">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Statistics Cards */}
          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card className="border-primary-200 bg-gradient-to-r from-primary-50 to-primary-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-primary-500 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-primary-600 font-medium">Total Customers</p>
                    <p className="text-2xl font-bold text-primary-800">{totalCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-gradient-to-r from-green-50 to-green-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-green-500 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-600 font-medium">Active Customers</p>
                    <p className="text-2xl font-bold text-green-800">
                      {customers.filter((c: Customer) => c.isActive).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">
                      {searchTerm ? 'Filtered Results' : 'This Page'}
                    </p>
                    <p className="text-2xl font-bold text-blue-800">
                      {searchTerm ? totalCount : customers.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div> */}
        </CardContent>
      </Card>
    </div>
  );
}